import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, Check, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { partnerBookingUrl } from '../lib/links';

/*
 * What an applicant sees the moment they submit.
 *
 * The ordering here is the whole point, and it is easy to get backwards. The calendar is
 * the PRIMARY thing on this screen; the emailed link is the safety net underneath it. If
 * booking waited on the email round-trip we would lose the bookings we just earned — the
 * applicant is never more willing than in the seconds after they hit submit.
 *
 * Calendly prefills from the URL: `name` and `email` are its built-in fields and the rest
 * are custom questions numbered a1..aN in the order the event lists them. The same
 * approach the marketing site uses (`components/marketing/calendly.ts`), and it has the
 * same caveat: the numbering is positional, so it must be checked against the live event
 * whenever the event's questions change. Built in `lib/links.js` — the portal header's
 * "Talk to Experts" button opens the same prefilled URL.
 */

const CALENDLY_WIDGET = 'https://assets.calendly.com/assets/external/widget.js';

const BookingScreen = () => {
  // From the session rather than a prop. Submitting the application signs them in, so the
  // details are already here, and reading them this way is what lets the page survive a
  // refresh instead of losing everything the moment somebody reloads.
  const { user, getAuthHeaders, API_URL } = useAuth();
  const applicant = user || {};
  const navigate = useNavigate();
  const [continuing, setContinuing] = useState(false);

  // The exit this screen did not have.
  //
  // Until 19 Aug 2026 the only way onward was "Already booked? Sign in", and that link could
  // not work: PublicRoute sends anyone with an unfinished application straight back here, so
  // it was a closed loop, and the only real way out was the emailed link. An applicant who
  // lost that email was stuck on a calendar with no way forward and no way to say so.
  //
  // They are already signed in — submitting the application is what signed them in — so we
  // can just mint their resume token and take them there. No email, no waiting.
  const continueApplication = async () => {
    setContinuing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/partner/application/resume-link`, {}, { headers: getAuthHeaders() });
      if (data.complete) {
        navigate('/dashboard');
        return;
      }
      navigate(`/continue?token=${encodeURIComponent(data.token)}`);
    } catch {
      // The email is the fallback for the fallback. If even this fails, say so plainly
      // rather than leaving them pressing a button that appears to do nothing.
      toast.error('Could not open your application. Check the link we emailed you, or try again.');
      setContinuing(false);
    }
  };

  // Calendly's inline widget hydrates any .calendly-inline-widget on the page once its
  // script has loaded. Appended rather than bundled because it is third-party and only
  // this screen needs it.
  useEffect(() => {
    if (document.querySelector(`script[src="${CALENDLY_WIDGET}"]`)) return undefined;
    const script = document.createElement('script');
    script.src = CALENDLY_WIDGET;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8] px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl border border-[#E2E2DF] bg-white p-8 shadow-sm">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#16a34a]/10">
            <Check className="h-5 w-5 text-[#16a34a]" strokeWidth={3} />
          </span>
          <h1 className="mt-5 text-2xl font-bold text-[#1A1A1A]">
            Your application is in. Now pick a time.
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#64748B]">
            Nobody screens you first. Choose a slot that suits you and we will go through what you
            run today, then show you a working Expedify built on your own vertical.
          </p>

          <div className="mt-7 overflow-hidden rounded-xl border border-[#E2E2DF]">
            <div
              className="calendly-inline-widget"
              data-url={partnerBookingUrl(applicant)}
              style={{ minWidth: '320px', height: '680px' }}
              data-testid="booking-calendly"
            />
            {/* If the third-party script is blocked, the embed above renders nothing at
                all. A plain link is the difference between a lost applicant and a booked
                call, so it is always in the markup rather than conditional on a failure
                we cannot reliably detect. */}
            <noscript>
              <a href={partnerBookingUrl(applicant)} className="block p-6 text-sm font-semibold text-[#ef785b]">
                Open the booking calendar
              </a>
            </noscript>
          </div>

          <p className="mt-4 text-center text-sm text-[#64748B]">
            Calendar not loading?{' '}
            <a
              href={partnerBookingUrl(applicant)}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-[#ef785b] hover:underline"
              data-testid="booking-fallback-link"
            >
              Open it in a new tab
            </a>
            .
          </p>
        </div>

        {/* The safety net. Second, deliberately. */}
        <div className="mt-6 rounded-2xl border border-[#E2E2DF] bg-white p-6">
          <div className="flex items-start gap-4">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ee785b]/10">
              <Mail className="h-4 w-4 text-[#ee785b]" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1A1A1A]">
                We have emailed you a link to finish your application
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#64748B]">
                It went to <span className="font-medium text-[#1A1A1A]">{applicant.email}</span>. A few
                more questions about your agency and your book, about two minutes, and you can stop
                and come back to it. What you answer there is what we build your demo from, so it is
                worth doing before the call. You choose a password at the end.
              </p>
            </div>
          </div>
        </div>

        {/* Booking is not a gate. The call is what we WANT them to do next, but the
            application is what we need, and an applicant who cannot make the calendar work
            — or has already booked — must still be able to carry on. */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={continueApplication}
            disabled={continuing}
            className="h-11 rounded-lg bg-[#ef785b] px-6 text-sm font-semibold text-white
                       transition-colors hover:bg-[#e56847] disabled:opacity-60"
          >
            {continuing ? 'Opening…' : 'Skip for now and finish your application'}
          </button>
          <p className="text-center text-xs text-[#64748B]">
            Already booked, or would rather do this first? Carry on here — it takes about two
            minutes and you choose your password at the end.
          </p>
          <div className="flex items-center gap-2 text-sm text-[#64748B]">
            <Calendar className="h-4 w-4" />
            <span>Already have a password?</span>
            <Link to="/login" className="font-semibold text-[#ef785b] hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingScreen;
